import { ExtendedTasksResponse } from 'app/console-api/tasks/tasks-extended';
import { BodyText, FlexColumn, FlexRow, SvgIcon, Tooltip } from '@make-software/cspr-design';
import LinkIcon from 'assets/icons/ic-link.svg';
import EditIcon from 'assets/icons/ic-edit.svg';
import ViewIcon from 'assets/icons/ic-view.svg';
import { TaskShortPreview } from 'app/components/management/apps/deals/view/task-short-preview';
import React from 'react';
import {
  StatsLink,
  StyledCardRow,
  StyledChipsTag,
  StyledSvgIcon,
  StyledTableData,
  StyledTooltip
} from 'app/components/common/common-styles';
import styled from 'styled-components';
import { formatDatetimeFull, formatDatetimeShort } from 'app/components/utils/measures-format';
import { describeAutomation, getIcon } from 'app/components/common/automation/registry';
import { TasksStatusChips } from 'app/components/common/tasks-statuses/tasks-status-chips';
import { PluginChip } from 'app/components/management/common/plugins/plugin-chip/plugin-chip';

const StatusWrapper = styled(FlexRow)<{ isActive?: boolean }>(({ theme, isActive }) =>
  theme.withMedia({
    backgroundColor: isActive ? '#31DE911A' : theme.styleguideColors.fillSecondary,
    borderRadius: '10px',
    padding: '0 8px',
    alignItems: 'center',
    justifyContent: 'center'
  })
);

const TypeWrapper = styled(StatusWrapper)(({ theme }) =>
  theme.withMedia({
    backgroundColor: theme.styleguideColors.fillSecondary
  })
);

interface TaskListItemProps {
  data: ExtendedTasksResponse;
  onView: (task: ExtendedTasksResponse) => void;
  onEdit: (task: ExtendedTasksResponse) => void;
  onStatistics?: (taskID: string) => void;
}

export const TaskListItem: React.FC<TaskListItemProps> = ({
  data,
  onView,
  onEdit,
  onStatistics
}) => {
  const handleViewClick = () => {
    onView(data);
  };

  const handleEditClick = () => {
    onEdit(data);
  };

  const handleOnStatistics = () => {
    if (onStatistics) {
      onStatistics(data.task.id);
    }
  };

  const categories =
    data.categories.length > 3 ? [...data.categories.slice(0, 3), '…'] : [...data.categories];

  return (
    <StyledCardRow key={data.task.id}>
      <StyledTableData>
        <TaskShortPreview name={data.task.name} description={data.task.description} />
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={4}>
          <FlexRow gap={42}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Status:
            </BodyText>
            <FlexRow gap={8}>
              <TasksStatusChips status={data.task.status} isActive={data.task.isActive} />
            </FlexRow>
          </FlexRow>
          <FlexRow gap={50} align={'center'}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Type:
            </BodyText>
            <FlexRow align={'center'} gap={4}>
              <PluginChip id={data.plugin.id}></PluginChip>
            </FlexRow>
          </FlexRow>
          <FlexRow gap={12}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Categories:
            </BodyText>
            <FlexRow gap={4} style={{ flexWrap: 'wrap' }}>
              {categories.map((cat, idx) => (
                <StyledChipsTag key={idx}>
                  <BodyText size={3}>{cat}</BodyText>
                </StyledChipsTag>
              ))}
            </FlexRow>
          </FlexRow>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={4} align={'flex-start'}>
          {data.automations.length > 0 && (
            <FlexRow gap={8}>
              <BodyText size={3} scale={'sm'} variation={'darkGray'}>
                Automations:
              </BodyText>
              <FlexRow align={'center'} gap={4}>
                {data.automations.map((automation) => (
                  <Tooltip tooltipContent={describeAutomation(automation)} key={automation.id}>
                    <SvgIcon src={getIcon(automation.type)} size={18} />
                  </Tooltip>
                ))}
              </FlexRow>
            </FlexRow>
          )}
          <FlexRow gap={38}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Created:
            </BodyText>
            <StyledTooltip tooltipContent={formatDatetimeFull(data.task.createdAt)}>
              <BodyText size={3} scale={'sm'}>
                {formatDatetimeShort(data.task.createdAt)}
              </BodyText>
            </StyledTooltip>
          </FlexRow>
          <FlexRow gap={34}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Updated:
            </BodyText>
            <StyledTooltip tooltipContent={formatDatetimeFull(data.task.updatedAt)}>
              <BodyText size={3} scale={'sm'}>
                {formatDatetimeShort(data.task.updatedAt)}
              </BodyText>
            </StyledTooltip>
          </FlexRow>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData>
        <FlexColumn gap={4}>
          <FlexRow gap={32}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Daily:
            </BodyText>
            <BodyText size={3} scale={'sm'}>
              {data.statistics.claimsDaily}
            </BodyText>
          </FlexRow>
          <FlexRow gap={12}>
            <BodyText size={3} scale={'sm'} variation={'darkGray'}>
              Monthly:
            </BodyText>
            <BodyText size={3} scale={'sm'}>
              {data.statistics.claimsMonthly}
            </BodyText>
          </FlexRow>
          <FlexRow gap={4} align={'center'}>
            <StatsLink size={3} scale={'sm'} variation={'darkGray'} onClick={handleOnStatistics}>
              More statistics <SvgIcon src={LinkIcon} size={12} />
            </StatsLink>
          </FlexRow>
        </FlexColumn>
      </StyledTableData>
      <StyledTableData align="right">
        <FlexRow gap={15}>
          <StyledSvgIcon src={ViewIcon} size={16} onClick={handleViewClick} />
          <StyledSvgIcon src={EditIcon} size={16} onClick={handleEditClick} />
        </FlexRow>
      </StyledTableData>
    </StyledCardRow>
  );
};
